import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ManageActions, CustomDelete, LocalDateRow } from 'components';
import { documentVersionActions } from 'store';
import { FindLoad, FindSuccessItem, Images, useModal, renderStaffName } from 'utils';
import { GET_DOCUMENT_VERSIONS, GET_DOCUMENT_VERSION_DETAIL, RECONSTRUCT_DOCUMENT_VERSION, RESTORE_DOCUMENT_VERSION } from 'store/documentVersion/documentVersion.types';
import { Loader } from 'components';
import { NoItemsYet } from '../../../../components';

const userName = (u) => (u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown' : 'Unknown');

const initials = (u) => {
    if (!u) return '?';
    const i = `${(u.firstName || '')[0] || ''}${(u.lastName || '')[0] || ''}`.toUpperCase();
    return i || (u.email || '?')[0].toUpperCase();
};

/**
 * Human name of the changed element — "1.Information › First Name".
 * Section part is the stored target.label; attribute part is resolved LIVE
 * from the current form by id so it works for old versions too.
 */
const changeName = (c, attrLabelById) => {
    const t = c.target || {};
    if (t.entity === 'ATTRIBUTE') {
        const live = t.attributeId ? attrLabelById.get(String(t.attributeId)) : '';
        const stored = c.after?.label || c.before?.label;
        const attr = live || stored;
        return attr ? `${t.label ? `${t.label} › ` : ''}${attr}` : t.label || 'attribute';
    }
    return t.label || t.field || (t.entity || '').toLowerCase();
};

export const VersionHistoryPanel = ({ formId, sections, open, onClose, onRestored, onPreview, previewingVersion, highlightChanges, onHighlightChangesToggle }) => {
    const dispatch = useDispatch();
    const { open: openModal } = useModal();
    const { versions, draftState: draft, reconstructed, versionDetail } = useSelector((state) => state.documentVersion);

    // attributeId → its current display label, for resolving change rows by id.
    const attrLabelById = useMemo(() => {
        const map = new Map();
        (sections || []).forEach((s) => (s?.attributes || []).forEach((a) => a?.id && map.set(String(a.id), a.label || a.name || '')));
        return map;
    }, [sections]);
    const loading = FindLoad(GET_DOCUMENT_VERSIONS);
    const reconstructLoading = FindLoad(RECONSTRUCT_DOCUMENT_VERSION);
    const restoreSuccess = FindSuccessItem(RESTORE_DOCUMENT_VERSION);
    const reconstructSuccess = FindSuccessItem(RECONSTRUCT_DOCUMENT_VERSION);
    const [expandedCards, setExpandedCards] = useState({});
    const [localHighlight, setLocalHighlight] = useState(false);
    const [loadingVersionNo, setLoadingVersionNo] = useState(null);
    const previewingVersionRef = useRef(null);
    const isHighlighting = highlightChanges !== undefined ? highlightChanges : localHighlight;

    // const toggleHighlight = () => {
    //     if (onHighlightChangesToggle) {
    //         onHighlightChangesToggle(!isHighlighting);
    //     } else {
    //         setLocalHighlight((prev) => !prev);
    //     }
    // };

    const params = { resourceModel: 'FORM', resourceId: formId };

    useEffect(() => {
        if (open && formId) {
            dispatch(documentVersionActions.getDocumentVersions(params));
            dispatch(documentVersionActions.getDraftState(params));
        }
        // eslint-disable-next-line
    }, [open, formId]);

    const toggleCard = (v) => {
        const willExpand = !expandedCards[v.versionNumber];
        setExpandedCards((prev) => ({ ...prev, [v.versionNumber]: willExpand }));
        // Fetch detail on first expand
        if (willExpand && v.id && !versionDetail[v.id]) {
            dispatch(documentVersionActions.getDocumentVersionDetail(v.id));
        }
    };

    const handlePreview = (v) => {
        if (!onPreview) return;
        previewingVersionRef.current = v;
        setLoadingVersionNo(v.versionNumber);
        dispatch(
            documentVersionActions.reconstructDocumentVersion({
                resourceModel: 'FORM',
                resourceId: formId,
                versionNumber: v.versionNumber,
            }),
        );
    };

    // React to reconstruct result from store — clone to prevent Redux mutation
    useEffect(() => {
        if (reconstructSuccess?.type && reconstructed && previewingVersionRef.current) {
            onPreview?.(JSON.parse(JSON.stringify(reconstructed)), previewingVersionRef.current);
            previewingVersionRef.current = null;
            setLoadingVersionNo(null);
        }
    }, [reconstructSuccess]);

    const handleRestore = (v) => {
        openModal(
            <CustomDelete
                info="Restore this version?"
                text="Your current template will revert to this version. Previous history will be preserved."
                innerText="Restore"
                color="blue"
                actionType={RESTORE_DOCUMENT_VERSION}
                handleDel={() =>
                    dispatch(
                        documentVersionActions.restoreDocumentVersion({
                            resourceModel: 'FORM',
                            resourceId: formId,
                            versionNumber: v.versionNumber,
                        }),
                    )
                }
            />,
        );
    };

    // React to restore result from store — clone to prevent Redux mutation
    useEffect(() => {
        if (restoreSuccess?.type && restoreSuccess?.data?.state) {
            onRestored?.(JSON.parse(JSON.stringify(restoreSuccess.data.state)));
        }
    }, [restoreSuccess]);

    if (!open) return null;
    const currentNo = versions[0]?.versionNumber;
    const isLoading = loading?.length > 0;

    return (
        <>
            <div className="vh-backdrop" onClick={onClose} />
            <div className="vh-panel">
                <div className="vh-header">
                    <div className="vh-title">
                        <img src={Images.formHistory} alt="formHistory" />
                        <span>Version history</span>
                    </div>
                    <button className="vh-close" onClick={onClose} aria-label="Close version history">
                        <img src={Images.closeSingleVector} alt="close" style={{ width: 18, height: 18 }} />
                    </button>
                </div>

                <div className="vh-body">
                    {isLoading && <Loader />}

                    {!isLoading && draft && (
                        <div className="vh-draft">
                            <div className="vh-draft-title">Unsaved Changes</div>
                            <div className="vh-draft-sub">
                                {draft.changeCount} edit{draft.changeCount === 1 ? '' : 's'} not saved to the form yet
                            </div>
                        </div>
                    )}

                    {!isLoading && !versions.length && !draft && (
                        <div className="properties-wrapper empty">
                            <p style={{marginBottom: 4}}>No versions yet.</p>
                            <p>Edit and Save to create one.</p>
                        </div>

                        // <NoItemsYet
                        //     text={`No versions yet.`}
                        //     containerStyles={{ padding: '20px 0' }}
                        // />

                        // <div className="vh-muted">No versions yet. Edit and Save to create one.</div>
                    )}

                    <div className="vh-cards-wrapper">
                        {!isLoading &&
                            versions.map((v) => {
                                const isCurrent = v.versionNumber === currentNo;
                                const isPreviewing = previewingVersion === v.versionNumber;
                                const isExpanded = !!expandedCards[v.versionNumber];
                                const changes = versionDetail[v.id] || [];
                                const hasChanges = changes.length > 0 || (v.summary && v.summary.length > 0);
                                const isLoadingPreview = loadingVersionNo === v.versionNumber && reconstructLoading?.length > 0;
                                const isLoadingDetail = isExpanded && !versionDetail[v.id];

                                return (
                                    <div
                                        key={v.id}
                                        className={`vh-card ${isCurrent ? 'is-current' : ''} ${isPreviewing ? 'is-previewing' : ''} ${
                                            isLoadingPreview ? 'is-loading' : ''
                                        } ${isExpanded ? 'is-expanded' : ''}`}>
                                        {/* Card Header */}
                                        <div className="vh-card-header" onClick={() => hasChanges && toggleCard(v)}>
                                            {hasChanges && (
                                                <button
                                                    className={`vh-chevron ${isExpanded ? 'open' : ''}`}
                                                    onClick={(e) => { e.stopPropagation(); toggleCard(v); }}
                                                    aria-label="Toggle version details">
                                                    <ChevronIcon />
                                                </button>
                                            )}

                                            {!hasChanges && <span className="vh-chevron-spacer" />}

                                            <span className="vh-avatar">{initials(v.savedBy)}</span>

                                            <div
                                                className="vh-card-main"
                                                style={{
                                                    cursor: isCurrent || reconstructLoading?.length > 0 ? 'default' : 'pointer',
                                                }}
                                                title={isCurrent ? '' : 'Click to preview this version'}
                                                // onClick={() => !isCurrent && !(reconstructLoading?.length > 0) && handlePreview(v)}
                                            >
                                                <div className="vh-row-time">
                                                    <div className="flex-align-center" style={{ gap: 8 }}>
                                                        <span className="vh-date-text">
                                                            <LocalDateRow date={v?.savedAt} />
                                                        </span>
                                                        <div className="vh-version-box">v{v?.versionNumber}</div>
                                                    </div>
                                                </div>
                                                {v.restoredFromVersion != null && (
                                                    <div className="vh-restored">
                                                        {v.label || (
                                                            <>
                                                                Restored from{' '}
                                                                {v.restoredFromDate ? (
                                                                    <LocalDateRow date={v.restoredFromDate} />
                                                                ) : (
                                                                    `v${v.restoredFromVersion}`
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="vh-sub">
                                                    <span className="vh-name">{renderStaffName(v?.savedBy)}</span>
                                                </div>

                                                {isCurrent && <div className="vh-current-badge">Current version</div>}
                                                {isPreviewing && <div className="vh-previewing-badge">Previewing</div>}
                                            </div>

                                            <div className="vh-menu-wrap" onClick={(e) => e.stopPropagation()}>
                                                {!isCurrent &&
                                                <ManageActions
                                                    styleBodyItems={{ padding: 0, width: 200 }}
                                                    btnIcon={Images.moreGrayIcon}
                                                    btnClass={'vh-kebab'}
                                                    anchorOrigin={{
                                                        vertical: 'bottom',
                                                        horizontal: 'right',
                                                    }}
                                                    transformOrigin={{
                                                        vertical: 'top',
                                                        horizontal: 'right',
                                                    }}
                                                    renderBody={(handleClose) => (
                                                        <div>
                                                            {isCurrent ? (
                                                                <div style={{ padding: '8px 16px', fontSize: 12, color: '#9ca3af' }}>
                                                                    Current version
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <div
                                                                        className="vh-menu-item"
                                                                        onClick={() => {
                                                                            handlePreview(v);
                                                                            handleClose();
                                                                        }}>
                                                                        <img src={Images.showEye} alt="formHistory" />
                                                                        <p>Preview this version</p>
                                                                    </div>
                                                                    <div
                                                                        className="vh-menu-item"
                                                                        onClick={() => {
                                                                            handleRestore(v);
                                                                            handleClose();
                                                                        }}>
                                                                        <img src={Images.formHistory} alt="formHistory" />
                                                                        <p>Restore this version</p>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    )}
                                                />
                                                }
                                            </div>
                                        </div>

                                        {/* Accordion Body — change rows */}
                                        {isExpanded && (
                                            <div className="vh-card-accordion">
                                                {isLoadingDetail ? (
                                                    <div className="vh-card-skeleton">
                                                        <div className="vh-skeleton-line vh-skeleton-line--long" />
                                                        <div className="vh-skeleton-line vh-skeleton-line--medium" />
                                                    </div>
                                                ) : changes.length > 0 ? (
                                                    <div className="vh-changes-list">
                                                        {changes.map((c, i) => (
                                                            <div key={i} className="vh-change">
                                                                <span className={`vh-tag t-${c.changeType}`}>{c.changeType}</span>
                                                                <span className="vh-change-field">{changeName(c, attrLabelById)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="vh-muted" style={{ padding: '8px 12px', fontSize: 12 }}>No details available</div>
                                                )}
                                            </div>
                                        )}

                                        {/* Skeleton loading when preview is loading */}
                                        {isLoadingPreview && (
                                            <div className="vh-card-skeleton" style={{marginLeft: 70}}>
                                                <div className="vh-skeleton-line vh-skeleton-line--long" />
                                                <div className="vh-skeleton-line vh-skeleton-line--medium" />
                                                <div className="vh-skeleton-line vh-skeleton-line--short" />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                </div>

                {/*<div className="vh-footer">*/}
                {/*    <label className="vh-highlight-label">*/}
                {/*        <Checkbox*/}
                {/*            checked={isHighlighting}*/}
                {/*            onChange={toggleHighlight}*/}
                {/*            sx={{*/}
                {/*                color: '#347AF0',*/}
                {/*                padding: '0',*/}
                {/*                '&.Mui-checked': { color: '#347AF0' },*/}
                {/*            }}*/}
                {/*        />*/}
                {/*        <span className="vh-highlight-text">Highlight changes</span>*/}
                {/*    </label>*/}
                {/*</div>*/}
            </div>
        </>
    );
};

// ── SVG Icons ────────────────────────────────────────────────────────────────

const ChevronIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4.5 2.5L8 6L4.5 9.5" />
    </svg>
);

